import React from 'react'
import { Col, Form, Row, Table } from 'react-bootstrap'
import { DateTime } from 'luxon'

import * as AppDefinitions from '../../App.d'
import * as Request from '../../utilities/request'
import * as Messages from '../ui/messages/messages'
import { PricingCard } from '../ui/pricingCard/PricingCard'

interface BillingFrameProps {
	show: boolean
	appState: AppDefinitions.AppState
}

export const BillingFrame = (props: BillingFrameProps) => {
	const [messages, updateMessages] = Messages.useMessageReducer([])
	const [portalUrl, setPortalUrl] = React.useState<string | null>(null)
	const subscription = props.appState.subscription

	React.useEffect(() => {
		const fetchPortalUrl = async () => {
			try {
				const req = await Request.get('subscription/portal', props.appState.authState)
				if (req.data?.portal?.url) {
					setPortalUrl(req.data.portal.url)
				}
			} catch (err) {
				console.log('Fetch portal url error:', err)
				updateMessages(Messages.addMessage('danger', 'Failed to load billing portal link'))
			}
		}

		if (props.appState.authState.isLoggedIn && props.show && subscription) {
			fetchPortalUrl()
		}
	}, [props.appState.authState.isLoggedIn, props.show, subscription])

	if (!props.show) {
		return null
	}

	const periodLabel =
		subscription?.currentPeriodStart && subscription?.currentPeriodEnd
			? `${DateTime.fromISO(subscription.currentPeriodStart).toLocaleString(DateTime.DATE_MED)} - ${DateTime.fromISO(
					subscription.currentPeriodEnd
			  ).toLocaleString(DateTime.DATE_MED)}`
			: '—'

	const planLabel = (subscription?.currentSubscriptionName ? 'LeaveCal' : 'No active subscription') + (subscription?.isTrial ? ' (Trial)' : '')
	const usageLabel = typeof subscription?.usage === 'number' ? subscription.usage : 'Loading...'

	const handleCheckoutError = (message: string) => {
		updateMessages(Messages.addMessage('danger', message))
	}

	return (
		<>
			<Form.Group style={{ padding: '30px' }}>
				<Row style={{ paddingBottom: '10px' }}>
					<Col style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<h3 style={{ margin: 0 }}>Billing</h3>
					</Col>
				</Row>
				{subscription === null && <p>Contact customer support to switch over to our new billing.</p>}
				{subscription !== null && (
					<>
						{subscription.cancelledAt === null ||
						(subscription.cancelledAt && DateTime.fromISO(subscription.cancelledAt).diffNow('seconds').seconds > 0) ? (
							<Table borderless responsive className="no-wrap">
								<thead>
									<tr className="stripe-row-body">
										<th className="header-sticky-top" style={{ fontWeight: 'bold' }}>
											Manage your subscription and billing information
										</th>
										<th />
									</tr>
								</thead>

								<tbody>
									<tr className="stripe-row">
										<td>Plan</td>
										<td>
											<strong>{planLabel}</strong>
										</td>
									</tr>

									<tr className="stripe-row stripe-row-body">
										<td>Number of employees</td>
										<td>{usageLabel}</td>
									</tr>

									<tr className="stripe-row">
										<td>Cost</td>
										<td>
											<strong>${subscription?.currentCost?.toFixed(2)}</strong> {subscription.isTrial ? '(after your trial)' : ''}
										</td>
									</tr>

									<tr className="stripe-row stripe-row-body">
										<td>Billing period</td>
										<td>{periodLabel}</td>
									</tr>

									<tr className="stripe-row">
										<td>Manage your subscription</td>
										<td>
											{portalUrl ? (
												<>
													<>
														{' '}
														<a href={portalUrl} target="_blank" rel="noreferrer">
															Open Billing Portal
														</a>
													</>
												</>
											) : null}
										</td>
									</tr>
								</tbody>
							</Table>
						) : (
							<>
								<p>You do not have a valid subscription</p>
								<PricingCard
									title="LeaveCal"
									description="Per-team member pricing"
									product="leavecal"
									usageLabel="employees"
									orgCurrency={props.appState.selectedOrg?.xeroOrg?.baseCurrency?.toLowerCase() || 'aud'}
									minValue={1}
									maxValue={100}
									defaultValue={10}
									authState={props.appState.authState}
									onCheckoutError={handleCheckoutError}
								/>
							</>
						)}
					</>
				)}
			</Form.Group>

			<Messages.Messages messages={messages} updateMessage={updateMessages} />
		</>
	)
}
